import { uniqBy } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';

export function useBIDatasetPicker(schemas) {
  const auth_store = useAuthStore();
  const route = useRoute();
  const asset = route.params.asset_id;
  const organization = auth_store.current_organization.uid;

  const hierarchicalSchemas = computed(() => {
    if (!(schemas.value || schemas))
      return [];
    const all_schemas = schemas.value || schemas;
    const roots = [];
    const nodeMap = new Map(); // key = fullPath string joined by '/'

    const getOrCreateFolder = (prefix, label, parentPrefix) => {
      let node = nodeMap.get(prefix);
      if (!node) {
        node = { uid: prefix, label, children: [] };
        nodeMap.set(prefix, node);
        if (parentPrefix) {
          const parent = nodeMap.get(parentPrefix);
          if (parent)
            parent.children.push(node);
          else roots.push(node);
        }
        else {
          roots.push(node);
        }
      }
      return node;
    };

    for (const schema of all_schemas) {
      if (!Array.isArray(schema.path) || schema.path.length === 0) {
        roots.push({
          uid: schema.id ?? schema.label ?? Math.random().toString(36).slice(2, 8),
          label: schema.label || schema.id,
          columns: schema.columns || [],
          _schema: schema,
        });
        continue;
      }

      let prefix = '';
      let parentPrefix = '';
      for (const part of schema.path) {
        const nextPrefix = prefix ? `${prefix}/${part}` : part;
        getOrCreateFolder(nextPrefix, part, parentPrefix || null);
        prefix = nextPrefix;
        parentPrefix = prefix;
      }

      // attach schema as a child of the final folder
      const schemaUid = `${prefix}::${schema.id ?? schema.label ?? Math.random().toString(36).slice(2, 8)}`;
      const leaf = {
        uid: schemaUid,
        label: schema.label || schemaUid,
        columns: schema.columns || [],
        _schema: schema,
      };
      (nodeMap.get(prefix)?.children || roots).push(leaf);
    }

    return roots;
  });

  function store_table_in_local_storage(table) {
    const bi_datasets = globalThis.localStorage.getItem(`bi_datasets`);
    let parsed_bi_datasets;
    if (bi_datasets) {
      parsed_bi_datasets = JSON.parse(bi_datasets);
      if (parsed_bi_datasets.organization !== organization) {
        parsed_bi_datasets = {};
      }
    }
    else {
      parsed_bi_datasets = {};
    }
    parsed_bi_datasets.organization = organization;
    if (!parsed_bi_datasets[asset])
      parsed_bi_datasets[asset] = [];
    parsed_bi_datasets[asset].unshift(table);
    parsed_bi_datasets[asset] = uniqBy(parsed_bi_datasets[asset], dataset => dataset.name);
    parsed_bi_datasets[asset] = parsed_bi_datasets[asset].slice(0, 20);
    globalThis.localStorage.setItem(`bi_datasets`, JSON.stringify(parsed_bi_datasets));
  }

  function get_tables_from_local_storage() {
    const bi_datasets = globalThis.localStorage.getItem(`bi_datasets`);
    let parsed_bi_datasets;
    if (bi_datasets) {
      parsed_bi_datasets = JSON.parse(bi_datasets);
    }
    else {
      parsed_bi_datasets = {};
    }
    return parsed_bi_datasets[asset];
  }

  return {
    store_table_in_local_storage,
    get_tables_from_local_storage,
    hierarchicalSchemas,
  };
}
